import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export interface AiSettings {
  provider: "anthropic" | "lovable_ai";
  model_starter: string;
  model_pro_research: string;
  model_pro_narrative: string;
  updated_at: string;
}

async function assertAdmin(userId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

export const getAiSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ settings: AiSettings }> => {
    await assertAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("ai_settings")
      .select("provider,model_starter,model_pro_research,model_pro_narrative,updated_at")
      .eq("id", "default")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) {
      return {
        settings: {
          provider: "anthropic",
          model_starter: "claude-sonnet-4-5-20250929",
          model_pro_research: "claude-opus-4-1-20250805",
          model_pro_narrative: "claude-opus-4-1-20250805",
          updated_at: new Date().toISOString(),
        },
      };
    }
    return { settings: data as AiSettings };
  });

const UpdateSchema = z.object({
  provider: z.enum(["anthropic", "lovable_ai"]),
  model_starter: z.string().trim().min(1).max(200),
  model_pro_research: z.string().trim().min(1).max(200),
  model_pro_narrative: z.string().trim().min(1).max(200),
});

export const updateAiSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => UpdateSchema.parse(data))
  .handler(async ({ context, data }): Promise<{ ok: true }> => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin
      .from("ai_settings")
      .upsert({ id: "default", ...data, updated_at: new Date().toISOString() });
    if (error) throw new Error(error.message);
    return { ok: true };
  });